// ProgramDashboard — composite synopsis surface for the student program
// workspace's Dashboard tab. The donut now lives in the dark hero band on the
// host page; this orchestrator focuses on what sits below it:
//
//   1. UpNextCard            (forest hero CTA, phase-aware, full-width)
//   2. Two-column body
//      LEFT  (1.5fr) — Timeline (vertical) + "Up next this week" (2x2 cards)
//      RIGHT (1fr)   — Deliverables stats + Team card + Announcements card

import { Platform, Text, View } from "react-native";
import { COLORS } from "../../../lib/brand";

import type { MyAnnouncement } from "../../../lib/announcements";
import type {
  Deliverable,
  LeaderboardRow,
  MyTeamPayload,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../../../types/database";
import {
  DashboardAnnouncementsCard,
  DashboardTeamCard,
} from "./DashboardSideCards";
import { DashboardDeliverablesStatsCard } from "./DashboardDeliverablesStatsCard";
import { DashboardTimelineList } from "./DashboardTimelineList";
import { UpNextCard, type CompetitionStatus } from "./UpNextCard";
import { UpNextWeekCards } from "./UpNextWeekCards";

const TEXT_MUTED = "rgba(28,43,26,0.62)";
const AMBER = COLORS.AMBER.hex;

export type ProgramDashboardProps = {
  competition: {
    id: string;
    status: CompetitionStatus;
    opens_at: string | null;
    closes_at: string | null;
    max_team_size: number;
  };
  myTeam: MyTeamPayload | null;
  deliverables: Deliverable[];
  submissions: TeamSubmissionRow[];
  statuses: TeamDeliverableStatusRow[];
  leaderboard: LeaderboardRow[];
  ownTeamRow: LeaderboardRow | null;
  announcements: MyAnnouncement[];

  onOpenTeam: () => void;
  onFindTeam: () => void;
  onPressDeliverable: (deliverableId: string) => void;
  onPressLeaderboard: () => void;
  onPressAnnouncements: () => void;
  onPressOneAnnouncement: (id: string) => void;
};

export function ProgramDashboard({
  competition,
  myTeam,
  deliverables,
  submissions,
  statuses,
  leaderboard,
  ownTeamRow,
  announcements,
  onOpenTeam,
  onFindTeam,
  onPressDeliverable,
  onPressLeaderboard,
  onPressAnnouncements,
  onPressOneAnnouncement,
}: ProgramDashboardProps) {
  const isCaptain = myTeam?.is_owner === true;
  const memberCount = myTeam ? myTeam.members.length : 0;

  return (
    <View style={{ gap: 18 }}>
      {/* ================================================================== */}
      {/* Up next dominant card — full width, phase-aware.                    */}
      {/* ================================================================== */}
      <UpNextCard
        status={competition.status}
        hasTeam={!!myTeam}
        deliverables={deliverables}
        submissions={submissions}
        ownTeamRow={ownTeamRow}
        closesAt={competition.closes_at}
        teamCount={leaderboard.length}
        onPressFindTeam={onFindTeam}
        onPressDeliverable={onPressDeliverable}
        onPressLeaderboard={onPressLeaderboard}
      />

      {/* ================================================================== */}
      {/* Two-column body — 1.5fr left / 1fr right on wide screens.           */}
      {/* ================================================================== */}
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "flex-start",
        }}
      >
        {/* LEFT — timeline + up-next-this-week cards */}
        <View style={{ minWidth: 320, flexBasis: 0, flexGrow: 1.5, gap: 18 }}>
          <Section title="Timeline" count={deliverables.length}>
            <DashboardTimelineList
              opensAt={competition.opens_at}
              closesAt={competition.closes_at}
              deliverables={deliverables}
              submissions={submissions}
              statuses={statuses}
            />
          </Section>

          <Section title="Up next this week">
            {/* UpNextWeekCards returns null when there's nothing actionable —
                hide the section heading too in that case. */}
            <UpNextWeekCards
              deliverables={deliverables}
              submissions={submissions}
              statuses={statuses}
              onPressDeliverable={onPressDeliverable}
            />
          </Section>
        </View>

        {/* RIGHT — stats + team + announcements */}
        <View style={{ minWidth: 280, flexBasis: 0, flexGrow: 1, gap: 14 }}>
          <DashboardDeliverablesStatsCard
            deliverables={deliverables}
            submissions={submissions}
            statuses={statuses}
          />

          <Section title="Team" count={memberCount > 0 ? memberCount : undefined}>
            <DashboardTeamCard
              myTeam={myTeam}
              isCaptain={isCaptain}
              maxTeamSize={competition.max_team_size}
              ownTeamRow={ownTeamRow}
              totalTeams={leaderboard.length}
              competitionStatus={competition.status}
              onOpenTeam={onOpenTeam}
              onFindTeam={onFindTeam}
            />
          </Section>

          <DashboardAnnouncementsCard
            announcements={announcements}
            onPressAll={onPressAnnouncements}
            onPressOne={onPressOneAnnouncement}
          />
        </View>
      </View>
    </View>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_700Bold",
            fontSize: 10.5,
            letterSpacing: 2.1,
            color: TEXT_MUTED,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Text>
        {count != null && count > 0 ? (
          <Text
            style={{
              fontFamily: "CormorantGaramond_500Medium",
              fontSize: 14,
              color: AMBER,
            }}
          >
            {count}
          </Text>
        ) : null}
      </View>
      {children}
    </View>
  );
}

// Keep Platform referenced so editors don't strip the import in the next
// edit pass. (The import is genuinely consumed only inside child components.)
void Platform;
